import { useEffect, useState } from "react";
import { Col, Nav, Row, Spinner } from "react-bootstrap"; // Removed raw Button import
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import AlertMessage from "../../../components/AlertMessage";
import { AlertType } from "../../../hooks/useAlert";
import { ApiErrorResponse } from "../../../types/ApiErrorResponse";
import ReportDefinitionDetailsTab from "../components/tabs/ReportDefinitionDetailsTab";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { GenericSaveButton } from "../../../components/common/GenericSaveButton";
import { ReportDefinitionFormDto, ReportDefinitionFormSelectorsDto } from "../types";
import { clearErrors, resetForm, setError, setErrors, setField, setForm } from "../../../redux/reportDefinitionSlice";
import { reportDefinitionFormSchema } from "../schema/reportDefinitionFormSchema";
import { createReportDefinition, getReportDefinitionById, getSelectorsData, updateReportDefinition } from "../api/reportDefinitionAPI";

const emptySelectors: ReportDefinitionFormSelectorsDto = {
    requestOptions: [],
    functionalAreaOptions: [],
    serviceOptions: [],
    organizationalStructureOptions: [],
};

export default function ReportDefinitionForm() {
    const { t } = useTranslation(["reportDefinition", "common"]);
    const navigate = useNavigate();
    const { reportDefinitionId } = useParams<{ reportDefinitionId: string }>();
    const isEditMode = Boolean(reportDefinitionId);
    const dispatch = useDispatch();

    const [selectors, setSelectors] = useState<ReportDefinitionFormSelectorsDto>(emptySelectors);
    const formData = useSelector((state: RootState) => state.reportDefinitionForm.data);
    
    const [loadingSelectors, setLoadingSelectors] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");   
    const [alertType, setAlertType] = useState<AlertType>("success");
    const [tab, setTab] = useState("definition");

    useEffect(() => {
        dispatch(resetForm());
        try {
            setLoadingSelectors(true);
            getSelectorsData()
            .then(selectorData => {
                    setSelectors(selectorData)
                })
                .catch((err: ApiErrorResponse) => {
                    setAlertMessage(err.message || t("common:error"));
                })
                .finally(() => setLoadingSelectors(false))
                
            if (reportDefinitionId) {
                setLoadingDetails(true);
                getReportDefinitionById(Number(reportDefinitionId))
                    .then(data => dispatch(setForm(data)))
                    .catch((err: ApiErrorResponse) => {
                        setAlertMessage(err.message || t("common:error"));
                    })
                    .finally(() => setLoadingDetails(false))
            }
        } catch (err: any) {
            setAlertMessage(err.message || t("common:error"));
            setLoadingSelectors(false);
            setLoadingDetails(false);
        }
    }, [reportDefinitionId]);
    
    const onSubmit = async () => {
        const schema = reportDefinitionFormSchema(t);
        dispatch(clearErrors());

        try {
            await schema.validate(formData, { abortEarly: false });
        } catch (err: any) {
            const validationErrors: { [key: string]: string | undefined } = {};
            if (err?.inner && Array.isArray(err.inner) && err.inner.length > 0) {
                err.inner.forEach((error: any) => {
                    if (error.path) {
                        validationErrors[error.path] = error.message;
                    }
                });
            } else if (err?.path) {
                validationErrors[err.path] = err.message;
            }
            dispatch(setErrors(validationErrors));
            return;
        }
        
        setLoadingDetails(true);
        try {
            if (isEditMode && reportDefinitionId) {
                await updateReportDefinition(Number(reportDefinitionId), formData);
            } else {
                await createReportDefinition(formData);
            }

            navigate("/admin/report-definitions", {
                state: {
                    message: t("reportDefinition:form.savedSuccessfully"),
                    shouldRefresh: true,
                },
            });
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            const errorMessage = fetchError?.message || t("common:error");
            setAlertType("danger");
            setAlertMessage(errorMessage);
            setShowAlert(true);
        } finally {
            setLoadingDetails(false);
        }
    };
    
    const onCancel = () => {
        dispatch(resetForm());
        navigate("/admin/report-definitions");
    };

    const updateField = <FormField extends keyof ReportDefinitionFormDto>(
        field: FormField,
        value: ReportDefinitionFormDto[FormField]
    ) => {
        dispatch(setField({ field, value }));
        dispatch(setError({ field, message: undefined }));
    };

    return(
        <div>
            {(loadingSelectors || loadingDetails) && (
                <div className="overlay-spinner">
                    <Spinner animation="border" variant="light" />
                </div>
            )}
            <Row className="mb-2">
                <Col xs={8} md={8}>
                    <h2>
                        {isEditMode
                            ? `${t("reportDefinition:form.editTitle")} #${reportDefinitionId}`
                            : t("reportDefinition:form.newTitle")}
                    </h2>
                </Col>
                <Col xs={4} md={4} className="text-end">
                    <GenericSaveButton 
                        onClick={onSubmit}
                        onCancelClick={onCancel}
                        cancelLabel={isEditMode ? t("common:buttons.buttonBack") : t("common:buttons.buttonCancel")}
                        saveLabel={t("common:save")}
                        disabled={loadingSelectors || loadingDetails}                    
                    />
                </Col>
            </Row>

            {showAlert && (
                <AlertMessage
                    message={alertMessage}
                    type={alertType}
                    onClose={() => setShowAlert(false)}
                />
            )}

            <Nav variant="tabs" activeKey={tab}>
                <Nav.Item>
                    <Nav.Link
                        eventKey="definition"
                        className={`survey-tab-link ${tab === "definition" ? "active-tab" : ""}`}
                        onClick={() => setTab("definition")}
                    >
                        {t("reportDefinition:form.tabs.definition")}
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link
                        eventKey="indicators"
                        className={`survey-tab-link ${tab === "indicators" ? "active-tab" : ""}`}
                        onClick={() => setTab("indicators")}
                    >
                        {t("reportDefinition:form.tabs.indicators")}
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link
                        eventKey="users"
                        className={`survey-tab-link ${tab === "users" ? "active-tab" : ""}`}
                        onClick={() => setTab("users")}
                    >
                        {t("reportDefinition:form.tabs.users")}
                    </Nav.Link>
                </Nav.Item>
            </Nav>

            <div className="mt-3">
                {tab === "definition" && (
                    <ReportDefinitionDetailsTab 
                        selectors={selectors} 
                        updateField={updateField}
                    />
                )}
            </div>
        </div>
    );
}